import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useProducerProfile } from "@/lib/producer-profile";
import { useAuth } from "@/features/auth/auth-context";
import { supabase } from "@/lib/supabaseClient";
import FortunaSVG from "@/assets/fortuna-logo.svg";
import { initiateYouTubeAuth } from '@/lib/youtube-auth'

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  /** Jump directly to a specific step when opening (optional) */
  forceStep?: 1 | 2 | 3;
  /**
   * When true, Step 2 (Contact) requires at least one of Email or Instagram
   * and hides the Skip button for that step. Used when opened from a feature gate.
   */
  requireContactOnThisOpen?: boolean;
  /**
   * Fires after contact is saved on Step 2 (normalized values).
   */
  onContactSaved?: (payload: { email: string | null; instagram_handle: string | null }) => void;
}

type Step = 1 | 2 | 3;

// ✅ Check YouTube connection
const checkYouTubeConnection = async (userId: string): Promise<boolean> => {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session.session?.access_token) return false;

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/youtube_tokens?select=access_token&user_id=eq.${userId}`,
      {
        headers: {
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY!,
          Authorization: `Bearer ${session.session.access_token}`,
          Accept: "application/json",
        },
      }
    );

    if (res.ok) {
      const data = await res.json();
      return !!data?.[0]?.access_token;
    }
    return false;
  } catch (err) {
    console.error("❌ YouTube connection check error:", err);
    return false;
  }
};

/* ✅ Generate YouTube OAuth URL
const generateYouTubeAuthUrl = (userId: string): string => {
  const clientId = import.meta.env.VITE_YOUTUBE_CLIENT_ID;
  const redirectUri =
    import.meta.env.VITE_YOUTUBE_REDIRECT_URI ||
    `${window.location.origin}/oauth-callback`;

  if (!clientId) {
    console.error("❌ Missing VITE_YOUTUBE_CLIENT_ID");
    return "#";
  }

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    access_type: "offline",
    scope: [
      "https://www.googleapis.com/auth/youtube.upload",
      "https://www.googleapis.com/auth/youtube.readonly",
    ].join(" "),
    prompt: "consent",
    state: userId,
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};
*/

export function OnboardingModal({
  open,
  onClose,
  forceStep,
  requireContactOnThisOpen = false,
  onContactSaved,
}: OnboardingModalProps) {
  const { user } = useAuth();
  const {
    displayName,
    setDisplayName,
    email: profileEmail,
    instagram_handle: profileIG,
  } = useProducerProfile();

  // Step state
  const [step, setStep] = useState<Step>(forceStep ?? 1);

  // Step 1: name
  const [nameInput, setNameInput] = useState(displayName ?? "");

  // Step 2: contact
  const [emailInput, setEmailInput] = useState(profileEmail ?? "");
  const [igInput, setIgInput] = useState(profileIG ?? "");
  const [contactError, setContactError] = useState<string | null>(null);
  const [savingContact, setSavingContact] = useState(false);

  // Step 3: YouTube
  const [checkingYouTube, setCheckingYouTube] = useState(false);
  const [hasYouTubeConnected, setHasYouTubeConnected] = useState(false);

  // Keep step/inputs in sync on each open
  useEffect(() => {
    if (open) {
      setStep(forceStep ?? 1);
      setNameInput(displayName ?? "");
      setEmailInput(profileEmail ?? "");
      setIgInput(profileIG ?? "");
      setContactError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, forceStep]);

  useEffect(() => {
    if (displayName && step === 1) setNameInput(displayName);
  }, [displayName, step]);

  useEffect(() => {
    const checkConnection = async () => {
      if (!user) return;
      setCheckingYouTube(true);
      const connected = await checkYouTubeConnection(user.id);
      setHasYouTubeConnected(connected);
      setCheckingYouTube(false);
    };

    if (open && user && step === 3) {
      checkConnection();

      const handleOAuthComplete = () => {
        setTimeout(() => checkConnection(), 1000);
      };

      window.addEventListener("youtube-oauth-complete", handleOAuthComplete);
      return () =>
        window.removeEventListener("youtube-oauth-complete", handleOAuthComplete);
    }
  }, [open, user, step]);

  const canSkipContact = useMemo(
    () => !requireContactOnThisOpen,
    [requireContactOnThisOpen]
  );

  const handleNameSubmit = async () => {
    if (!nameInput.trim()) return;
    await setDisplayName(nameInput.trim());
    setStep(2);
  };

  // grab profile updater from the same hook to keep cache hot
  const { updateContacts } = useProducerProfile();

  const handleContactSubmit = async () => {
    setContactError(null);
    const email = emailInput.trim() || null;
    const ig = igInput.trim() || null;

    if (requireContactOnThisOpen && !email && !ig) {
      setContactError("Add at least an email or Instagram handle.");
      return;
    }

    // If nothing entered and skipping is allowed, proceed
    if (!email && !ig && canSkipContact) {
      setStep(3);
      return;
    }

    // lightweight email check
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setContactError("Please enter a valid email address.");
      return;
    }

    setSavingContact(true);

    // ensure latest name is persisted if changed
    if (nameInput.trim() && nameInput.trim() !== (displayName ?? "")) {
      await setDisplayName(nameInput.trim());
    }

    const normalizedIg = ig ? ig.replace(/^@/, "") : null;

    const { error } = await updateContacts({
      email,
      instagram_handle: normalizedIg,
    });

    setSavingContact(false);

    if (error) {
      console.error("❌ Saving contact failed", error);
      setContactError("Could not save your contact info. Please try again.");
      return;
    }

    // notify parent immediately with normalized values
    onContactSaved?.({
      email,
      instagram_handle: normalizedIg,
    });

    setStep(3);
  };

  const handleClose = () => {
    onClose(); // Always allow close; gating happens at point-of-use.
  };

  const handleSkipContact = () => {
    if (canSkipContact) setStep(3);
  };

  const handleSkipYouTube = () => {
    onClose(); // optional step
  };


  return (
    <Dialog open={open} onOpenChange={(val) => val === false && handleClose()}>
      <DialogContent className="max-w-md p-6 text-center">
        <div className="flex flex-col items-center gap-3 mb-4">
          <img src={FortunaSVG} alt="Fortuna Logo" className="w-36 h-24" />
          <h2 className="text-xl font-bold">
            {step === 1 ? "Welcome to Fortuna" : step === 2 ? "Add a contact" : "Almost done!"}
          </h2>
          <p className="text-xs text-muted-foreground">Step {step} of 3</p>
        </div>

        {step === 1 && (
          <>
            <p className="text-muted-foreground mb-4 text-sm">
              What should we call you? This name will appear on your posts.
            </p>
            <Input
              placeholder="Enter your producer name"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="mb-4"
              onKeyDown={(e) => e.key === "Enter" && handleNameSubmit()}
            />
            <Button
              disabled={!nameInput.trim()}
              onClick={handleNameSubmit}
              className="w-full"
            >
              Continue
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-muted-foreground mb-4 text-sm">
              Add <strong>one</strong> way for artists to reach you. You can edit this later.
            </p>
            <div className="space-y-3 text-left">
              <div>
                <label className="text-sm">Email</label>
                <Input
                  type="email"
                  placeholder="you@domain.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm">Instagram handle</label>
                <Input
                  placeholder="yourhandle (no @)"
                  value={igInput}
                  onChange={(e) => setIgInput(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  We’ll format the link automatically.
                </p>
              </div>

              {contactError && (
                <p className="text-red-500 text-sm">{contactError}</p>
              )}
            </div>

            <div className="mt-4 space-y-2">
              <Button
                className="w-full"
                onClick={handleContactSubmit}
                disabled={savingContact}
              >
                {savingContact ? "Saving..." : "Continue"}
              </Button>

              {canSkipContact && (
                <Button
                  variant="ghost"
                  onClick={handleSkipContact}
                  className="w-full text-muted-foreground"
                >
                  Skip for now
                </Button>
              )}
            </div>
          </>
        )}

        {step === 3 && (
  <>
    <p className="text-muted-foreground mb-4 text-sm">
      Connect your YouTube account to start uploading beats.
    </p>

    {hasYouTubeConnected ? (
      <div className="space-y-3">
        <div className="text-green-600 font-medium">
          ✅ YouTube Connected!
        </div>
        <Button onClick={handleClose} className="w-full">
          Complete Setup
        </Button>
      </div>
    ) : (
      <div className="space-y-3">
        <Button 
          onClick={() => {
            console.log("🔍 Initiating YouTube OAuth");
            initiateYouTubeAuth();
          }}
          disabled={checkingYouTube} 
          className="w-full"
        >
          {checkingYouTube ? "Checking Connection..." : "Connect YouTube"}
        </Button>

        <Button
          variant="ghost"
          onClick={handleSkipYouTube}
          className="w-full text-muted-foreground"
        >
          Skip for now
        </Button>
      </div>
    )}
  </>
)}
      </DialogContent>
    </Dialog>
  );
}
